import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { CalendarBlank, HardHat, Warning, CheckCircle, TrendUp, ListChecks, Sparkle, Receipt, ArrowsClockwise, Wrench, Envelope, Users, Plug, CaretDown } from "@phosphor-icons/react";
import TechHoursCard from "@/components/TechHoursCard";
import WeatherWidget from "@/components/WeatherWidget";
import PayrollCard from "@/components/PayrollCard";
import TresoreriePanel from "@/components/TresoreriePanel";
import ChantiersMapPanel from "@/components/ChantiersMapPanel";
import ComptaHoursKmPanel from "@/components/ComptaHoursKmPanel";

const STATUS_META = {
  waiting: { label: "En attente", color: "#9CA3AF" },
  in_progress: { label: "En cours", color: "#005CFF" },
  urgent: { label: "Urgent", color: "#EF4444" },
  done: { label: "Terminé", color: "#10B981" },
};

export default function Dashboard({ onCreate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "compta" || user?.role === "associate";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expanded, setExpanded] = useState(null); // 'chantiers' | 'today' | 'tasks' | 'urgent' | null
  const [hiddenBlocks, setHiddenBlocks] = useState([]);
  const DEFAULT_ORDER = ["weather", "kpis", "status", "chantiers_map", "tresorerie", "compta_hk", "hours", "payroll", "recent", "ai_prompt"];
  const [blockOrder, setBlockOrder] = useState(DEFAULT_ORDER);

  useEffect(() => {
    (async () => {
      const [d, c] = await Promise.all([api.get("/dashboard"), api.get("/chantiers")]);
      setData(d.data);
      setChantiers(c.data);
    })();
    // Fetch personal dashboard prefs (hidden + order) — silent fallback if it fails
    api.get("/users/me/dashboard-prefs")
      .then((r) => {
        setHiddenBlocks(r.data?.hidden || []);
        if (Array.isArray(r.data?.order) && r.data.order.length) {
          // Merge saved order with defaults so new blocks appended in code are
          // still surfaced even for users who customised their dashboard before.
          const saved = r.data.order;
          const merged = [...saved, ...DEFAULT_ORDER.filter((k) => !saved.includes(k))];
          setBlockOrder(merged);
        }
      })
      .catch(() => setHiddenBlocks([]));
  }, []);

  const isHidden = (key) => hiddenBlocks.includes(key);

  // Lazy load pending tasks only when the KPI is opened
  useEffect(() => {
    if (expanded !== "tasks" || tasks.length > 0) return;
    (async () => {
      try {
        const r = await api.get("/tasks/pending");
        setTasks(r.data || []);
      } catch {
        setTasks([]);
      }
    })();
  }, [expanded, tasks.length]);

  // Memoise the visible-ordered blocks so `blockMap` isn't recomputed on
  // every render just for the filter/order (flagged by code review linter).
  const visibleBlockKeys = useMemo(
    () => blockOrder.filter((k) => !hiddenBlocks.includes(k)),
    [blockOrder, hiddenBlocks],
  );

  if (!data) return <SkeletonDash />;

  const urgentChantiers = chantiers.filter((c) => c.status === "urgent");
  const activeChantiers = chantiers.filter((c) => c.status !== "done");
  const recentChantiers = chantiers.slice(0, 5);
  const toggle = (k) => setExpanded((e) => (e === k ? null : k));

  // Block definitions keyed by pref key. Rendered in `blockOrder`, skipped if hidden.
  const blockMap = {
    weather: <WeatherWidget />,
    kpis: (
      <>
        <div className="grid grid-cols-2 gap-3">
          <KPI icon={HardHat} label="Chantiers" value={data.chantiers_total} accent="#005CFF" testid="kpi-chantiers" active={expanded === "chantiers"} onClick={() => toggle("chantiers")} />
          <KPI icon={CalendarBlank} label="Aujourd'hui" value={data.today_interventions.length} accent="#10B981" testid="kpi-today" active={expanded === "today"} onClick={() => toggle("today")} />
          <KPI icon={ListChecks} label="Tâches en attente" value={data.pending_tasks} accent="#F59E0B" testid="kpi-tasks" active={expanded === "tasks"} onClick={() => toggle("tasks")} />
          <KPI icon={Warning} label="Urgents" value={data.chantiers_by_status.urgent || 0} accent="#EF4444" testid="kpi-urgent" active={expanded === "urgent"} onClick={() => nav("/alertes")} />
        </div>
        {expanded === "chantiers" && (
          <ExpandPanel title={`Tous les chantiers actifs (${activeChantiers.length})`} testid="expand-chantiers">
            {activeChantiers.length === 0
              ? <EmptyLine>Aucun chantier actif</EmptyLine>
              : activeChantiers.map((c) => <ChantierLine key={c.id} c={c} onClick={() => nav(`/chantiers/${c.id}`)} />)}
          </ExpandPanel>
        )}
        {expanded === "today" && (
          <ExpandPanel title={`Interventions d'aujourd'hui (${data.today_interventions.length})`} testid="expand-today">
            {data.today_interventions.length === 0
              ? <EmptyLine>Rien de prévu aujourd&apos;hui</EmptyLine>
              : data.today_interventions.map((iv) => (
                  <div key={iv.id} className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5 flex items-center gap-3" data-testid={`expand-iv-${iv.id}`}>
                    <div className="w-1 h-9 rounded-full bg-[#10B981]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{iv.title}</div>
                      <div className="text-[11px] text-white/50">
                        {new Date(iv.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {iv.duration_min} min
                      </div>
                    </div>
                  </div>
                ))}
          </ExpandPanel>
        )}
        {expanded === "tasks" && (
          <ExpandPanel title={`Tâches en attente (${data.pending_tasks})`} testid="expand-tasks">
            {tasks.length === 0
              ? <EmptyLine>Toutes les tâches sont faites 🎉</EmptyLine>
              : tasks.slice(0, 20).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => t.chantier_id && nav(`/chantiers/${t.chantier_id}`)}
                    className="w-full text-left rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 flex items-center gap-3 hover:border-white/20"
                    data-testid={`expand-task-${t.id}`}
                  >
                    <div className="w-2 h-2 rounded-full bg-[#F59E0B] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{t.label}</div>
                      {t.chantier_name && <div className="text-[10px] text-white/40 truncate">{t.chantier_name}</div>}
                    </div>
                  </button>
                ))}
          </ExpandPanel>
        )}
        {expanded === "urgent" && (
          <ExpandPanel title={`Chantiers urgents (${urgentChantiers.length})`} testid="expand-urgent">
            {urgentChantiers.length === 0
              ? <EmptyLine>Aucun chantier urgent — respire 😌</EmptyLine>
              : urgentChantiers.map((c) => <ChantierLine key={c.id} c={c} onClick={() => nav(`/chantiers/${c.id}`)} />)}
          </ExpandPanel>
        )}
      </>
    ),
    status: (
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-white/50">Répartition</div>
          <TrendUp size={16} className="text-white/40" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <div key={k} className="rounded-xl bg-white/[0.03] p-3">
              <div className="w-2 h-2 rounded-full mb-2" style={{ background: m.color }} />
              <div className="text-xl font-bold">{data.chantiers_by_status[k] || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    hours: <TechHoursCard />,
    payroll: <PayrollCard />,
    tresorerie: isAdmin ? <TresoreriePanel /> : null,
    chantiers_map: isAdmin ? <ChantiersMapPanel /> : null,
    compta_hk: isAdmin ? <ComptaHoursKmPanel /> : null,
    recent: (
      <div>
        <SectionTitle>Chantiers récents</SectionTitle>
        {recentChantiers.length === 0 ? (
          <button onClick={onCreate} className="w-full rounded-2xl bg-white/[0.03] border border-dashed border-white/15 p-8 text-center active:scale-95 transition-transform" data-testid="empty-create-chantier" aria-label="Creer un chantier">
            <HardHat size={28} className="mx-auto text-[#005CFF] mb-2" weight="duotone" />
            <div className="font-medium">Créer votre premier chantier</div>
            <div className="text-xs text-white/50 mt-1">Appuyez pour démarrer</div>
          </button>
        ) : (
          <div className="space-y-2">
            {recentChantiers.map((c) => (
              <button
                key={c.id}
                onClick={() => nav(`/chantiers/${c.id}`)}
                className="w-full text-left rounded-2xl bg-white/[0.03] border border-white/10 p-4 hover:border-white/20 active:scale-[0.98] transition-transform"
                data-testid={`dash-chantier-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="w-1.5 h-10 rounded-full shrink-0 mt-0.5" style={{ background: c.color || STATUS_META[c.status]?.color || "#9CA3AF" }} data-testid={`dash-dot-${c.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-white/50 mt-0.5 truncate">{c.address || "Adresse non renseignée"}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap" style={{
                    background: `${STATUS_META[c.status]?.color}22`,
                    color: STATUS_META[c.status]?.color,
                  }}>
                    {STATUS_META[c.status]?.label}
                  </span>
                </div>
                <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.progress}%`, background: STATUS_META[c.status]?.color }} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    ),
    ai_prompt: (
      <button
        onClick={() => nav("/ia")}
        className="w-full rounded-2xl bg-gradient-to-br from-[#005CFF]/20 to-[#005CFF]/5 border border-[#005CFF]/30 p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
        data-testid="dash-ai-shortcut"
      >
        <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
          <Sparkle size={22} weight="fill" color="white" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-display font-semibold">Assistant IA technique</div>
          <div className="text-xs text-white/60">Branchement, notice, code d&apos;erreur…</div>
        </div>
      </button>
    ),
  };

  return (
    <div className="p-4 space-y-5">
      {/* Welcome */}
      <div>
        <div className="text-white/50 text-xs uppercase tracking-widest">Bonjour</div>
        <div className="font-display text-3xl font-bold mt-1" data-testid="dash-welcome">{user?.name}</div>
        <div className="text-white/50 text-sm mt-1">
          {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>

      {/* Blocks rendered in user's preferred order — hidden ones skipped */}
      {visibleBlockKeys.map((k) => blockMap[k] ? (
        <div key={k} data-testid={`dash-block-render-${k}`}>{blockMap[k]}</div>
      ) : null)}

      {/* Manager cockpit — visible only to admin/associate/compta (not part of the reorderable set) */}
      {data.manager_stats && ["admin", "compta", "associate"].includes(user?.role) && (
        <ManagerCockpit stats={data.manager_stats} nav={nav} />
      )}
    </div>
  );
}

function ExpandPanel({ title, children, testid }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 px-1 pb-1">{title}</div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function ChantierLine({ c, onClick }) {
  const dotColor = c.color || STATUS_META[c.status]?.color || "#9CA3AF";
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5 hover:border-white/20 active:scale-[0.98] transition-transform"
      data-testid={`expand-ch-${c.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-1.5 h-8 rounded-full shrink-0" style={{ background: dotColor }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{c.name}</div>
          <div className="text-[11px] text-white/50 truncate">{c.address || "Adresse non renseignée"} · {c.progress || 0}%</div>
        </div>
        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap" style={{
          background: `${STATUS_META[c.status]?.color}22`,
          color: STATUS_META[c.status]?.color,
        }}>{STATUS_META[c.status]?.label}</span>
      </div>
    </button>
  );
}

function EmptyLine({ children }) {
  return <div className="text-xs text-white/40 text-center py-4">{children}</div>;
}

function ManagerCockpit({ stats, nav }) {
  const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  const checklist = stats.config_checklist || {};
  const checklistItems = [
    { k: "bkpay_configured", label: "Bridge BKpay activé", tab: "bkpay" },
    { k: "webhooks_configured", label: "Webhooks branchés", tab: "webhooks" },
    { k: "axonaut_configured", label: "Axonaut connecté", tab: "axonaut" },
    { k: "twilio_configured", label: "SMS Twilio configuré", tab: "twilio" },
    { k: "team_size_ok", label: "Équipe créée", tab: "team" },
  ];

  return (
    <div className="space-y-3" data-testid="manager-cockpit">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-transparent border border-emerald-500/30 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Cockpit gérant · {stats.month}</div>
        <div className="font-display font-bold text-3xl">{eur(stats.ca_signed_this_month)}</div>
        <div className="text-xs text-white/60 mt-1">CA de devis signés ce mois · {stats.quotes_signed_count} devis</div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MiniStat icon={CalendarBlank} label="Inter. mois" value={`${stats.interventions_done_this_month}/${stats.interventions_this_month}`} color="#005CFF" testid="mgr-iv" />
        <MiniStat icon={HardHat} label="Chantiers OK" value={stats.chantiers_completed_this_month} color="#10B981" testid="mgr-ch" />
        <MiniStat icon={Envelope} label="Ouverture" value={`${stats.reports_open_rate}%`} color="#8B5CF6" testid="mgr-open" />
        <MiniStat icon={Wrench} label="Alertes équip." value={stats.equipment_alerts} color={stats.equipment_overdue > 0 ? "#EF4444" : "#F59E0B"} testid="mgr-eq" onClick={() => nav("/admin?tab=equipment")} />
        <MiniStat icon={ArrowsClockwise} label="BKpay attente" value={stats.bkpay_outbox_pending} color={stats.bkpay_outbox_failed > 0 ? "#EF4444" : "#94A3B8"} testid="mgr-bkpay" onClick={() => nav("/admin?tab=bkpay")} />
        <MiniStat icon={Receipt} label="Devis attente" value={stats.quotes_pending} color="#F59E0B" testid="mgr-quotes" onClick={() => nav("/admin?tab=quotes")} />
      </div>

      {stats.top_techs && stats.top_techs.length > 0 && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Top techniciens ce mois</div>
          <div className="space-y-1.5">
            {stats.top_techs.map((t, i) => (
              <div key={t._id || i} className="flex items-center gap-2 text-sm">
                <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-[10px] font-bold text-white/60">{i + 1}</div>
                <div className="flex-1 truncate">{t.name}</div>
                <div className="text-white/60 tabular-nums text-xs">{(t.hours || 0).toFixed(1)} h · {t.count} interv.</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-white/50">Checklist configuration</div>
          <button onClick={() => nav("/admin")} className="text-[11px] text-[#7CB2FF] hover:underline">Ouvrir la console →</button>
        </div>
        <div className="space-y-1.5">
          {checklistItems.map((it) => {
            const done = !!checklist[it.k];
            return (
              <div key={it.k} className="flex items-center gap-2 text-xs">
                <div className={`w-4 h-4 rounded-full border ${done ? "bg-emerald-500 border-emerald-500" : "bg-white/5 border-white/20"} flex items-center justify-center shrink-0`}>
                  {done && <CheckCircle size={10} weight="fill" className="text-white" />}
                </div>
                <span className={done ? "text-white/70 line-through" : "text-white/90"}>{it.label}</span>
                {!done && <button onClick={() => nav(`/admin?tab=${it.tab}`)} className="ml-auto text-[10px] text-[#7CB2FF] hover:underline">Configurer</button>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <QuickAction icon={ArrowsClockwise} label="Bridge BKpay" onClick={() => nav("/admin?tab=bkpay")} accent="#10B981" testid="qa-bkpay" />
        <QuickAction icon={Wrench} label="Outillage" onClick={() => nav("/admin?tab=equipment")} accent="#F59E0B" testid="qa-equip" />
        <QuickAction icon={Plug} label="Intégrations" onClick={() => nav("/admin?tab=axonaut")} accent="#005CFF" testid="qa-integ" />
        <QuickAction icon={Users} label="Console gérant" onClick={() => nav("/admin")} accent="#8B5CF6" testid="qa-console" />
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, color, testid, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick} className={`rounded-2xl bg-white/[0.03] border border-white/10 p-3 text-left ${onClick ? "active:scale-95 hover:border-white/20" : ""}`} data-testid={testid}>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center mb-2" style={{ background: color + "22" }}>
        <Icon size={14} weight="duotone" color={color} />
      </div>
      <div className="font-display font-bold text-lg leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-white/50 mt-1.5">{label}</div>
    </Comp>
  );
}

function QuickAction({ icon: Icon, label, onClick, accent, testid }) {
  return (
    <button onClick={onClick} className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 p-3 flex items-center gap-2 active:scale-95" data-testid={testid}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: accent + "22", color: accent }}>
        <Icon size={16} weight="duotone" />
      </div>
      <span className="text-sm font-medium truncate">{label}</span>
    </button>
  );
}

function KPI({ icon: Icon, label, value, accent, testid, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 transition-all active:scale-[0.97] ${active ? "bg-white/[0.06] border-white/25 ring-1 ring-inset ring-white/10" : "bg-white/[0.03] border-white/10 hover:border-white/20"}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${accent}22` }}>
          <Icon size={20} weight="duotone" color={accent} />
        </div>
        <CaretDown size={12} className={`text-white/40 transition-transform ${active ? "rotate-180" : ""}`} />
      </div>
      <div className="text-3xl font-display font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-2">{label}</div>
    </button>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">{children}</div>;
}

function SkeletonDash() {
  return (
    <div className="p-4 space-y-4 animate-pulse">
      <div className="h-8 w-40 bg-white/5 rounded-lg" />
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-2xl bg-white/5" />)}
      </div>
      <div className="h-40 rounded-2xl bg-white/5" />
    </div>
  );
}
